import { Manifest } from "../../../../types";
import { IconDescriptor, AppIcons, ComponentConfig } from "../types";
import { AppDIcon } from "../../Interop/types";
import { Logger } from "../../../../clients/logger";

const isAllowedMimeType = (type: string) =>
	["image/webp", "image/svg+xml", "image/tiff", "image/svg+xml", "image/png", "image/jpeg", "image/jpg"].includes(type);
/**
 * Returns an icon if Finsemble's legacy foreign Toolbar config has been set. This config
 * is the only way that a font can be set as an icon.
 *
 * @param appConfig - The app's config
 * @returns An IconDescriptor that is compatible with Finsemble's UI or undefined if no
 * legacy config has been set.
 */
const maybeLegacyToolbarIcon = (appConfig: ComponentConfig): IconDescriptor | undefined => {
	const legacyToolbarConfig = appConfig?.foreign?.components?.Toolbar;

	const legacyToolbarIconAsURL: IconDescriptor | undefined = legacyToolbarConfig?.iconURL
		? {
				imageType: "url",
				path: legacyToolbarConfig.iconURL,
			}
		: undefined;

	const legacyToolbarIconAsClass: IconDescriptor | undefined = legacyToolbarConfig?.iconClass
		? {
				imageType: "fonticon",
				path: legacyToolbarConfig.iconClass,
			}
		: undefined;

	return legacyToolbarIconAsURL ?? legacyToolbarIconAsClass;
};

/**
 * Extract an icon's path from an AppD "icons" section.
 *
 * Previous FDC3 versions used "icon". The current spec uses "src". A few components
 * have inadvertently used "url". This function will accept any of these.
 *
 * @param icon - An FDC3 AppD icon definition (one icon from the "icons" array)
 * @returns The icon's path or "" if the path is missing
 */
const appDIconSrc = (icon: AppDIcon): string => {
	const src = icon.src ?? (icon as any).url ?? (icon as any).icon;
	if (!src) Logger.warn("Invalid AppD icon: `src` is missing", icon);
	return src ?? "";
};

/**
 * From a set of icons, pick the icon that is closest to 16 pixels.
 *
 * @param iconsConfig - The array of icons from an AppD "icons" section
 * @returns An IconDescriptor object that can be rendered by Finsemble's UI
 */
const pickAppDIconBySize = (iconsConfig: AppDIcon[]): IconDescriptor => {
	// Sort where closer to 16 is better (front of array)
	const best = iconsConfig.sort((a, b) => {
		if (a.sizes && b.sizes) {
			const aDiff = parseInt(a.sizes) - 16;
			const bDiff = parseInt(b.sizes) - 16;
			return Math.abs(aDiff) - Math.abs(bDiff);
		}
		// An icon with sizes specified is better (lower score)
		if (a.sizes && !b.sizes) return -1;
		if (b.sizes && !a.sizes) return 1;
		return 0;
	})[0];

	return {
		imageType: "url",
		path: appDIconSrc(best),
	};
};

/**
 * Compute the absolute path for icons with relative paths (based on the url of the app's AppD configuration).
 *
 * Note that native apps don't provide a url and therefore cannot use relative paths.
 *
 * @param src - The path of the icon ("src" field from an AppD "icons" entry)
 * @param appConfig - The AppD config for the icon's app.
 * @returns The absolute path of the icon, or the original src if the icon is not a relative path.
 */
const absolutePath = (src: string, appConfig: ComponentConfig): string => {
	if (!src) return src;
	const appURL = appConfig.window?.url;
	// There may not be a base url, for instance if this is a native app.
	if (!appURL) return src;

	// If the path is already absolute then do nothing
	if (src.includes("http://") || src.includes("https://")) return src;

	// Compute the absolute path. If there is some sort of error (like an invalid url) then just return the original
	try {
		const resolvedURL = new URL(src, appURL);
		return resolvedURL.href;
	} catch (e) {
		Logger.warn(`Cannot compute absolute path for icon in app ${appConfig?.component?.type}`, src);
		return src;
	}
};

/**
 * Windows taskbar only accepts png, jpg or ico formats.
 * @param icon - An AppD icon entry
 * @returns True if the icon can be displayed in the MS Windows taskbar
 */
const isTaskbarCompatible = (icon: AppDIcon): boolean => {
	if (!icon?.src) return false;
	const path = icon.src.toLowerCase();
	return path.endsWith(".png") || path.endsWith(".jpg") || path.endsWith(".jpeg") || path.endsWith(".ico");
};

/**
 * Checks whether an icon is deemed web compatible or not against a set of
 * allowed mime types.
 *
 * Additionally, prevents .ico and .icns formats in AppD icon entries.
 *
 * @param icon - An AppD icon entry
 * @returns True if the icon can be displayed in a browser
 */
const isWebCompatible = (icon: AppDIcon): boolean => {
	if (!icon?.src) return true;

	// Rely on the type field if configured, and check against allowed mime types
	if (icon?.type) {
		Logger.debug(`Checking for ${icon.type} in allowedMimeTypes`);
		return isAllowedMimeType(icon.type);
	}

	// We're making a best-effort approach to check for filetype without
	// resorting to download the file and check its content-type.

	// Note that this method will return true if we do not have a type configured
	// and the icon.path points to an endpoint (without file extensions)
	// that returns an image.
	const path = icon.src.toLowerCase();
	// Check the configured src field is a valid URL and..
	try {
		Logger.debug(`Checking for ${path} is valid URL and validating file extension`);
		const iconURL = new URL(path);
		// check whether we have a search string (e.g. ?foo=bar.png) otherwise check full path
		return iconURL.search
			? !iconURL.search.includes(".ico") && !iconURL.search.includes(".icns")
			: !path.endsWith(".ico") && !path.endsWith(".icns");
	} catch (error) {
		Logger.debug(`Invalid URL path ${path} configured`);
		return false;
	}
};

/**
 * Returns the best available icon, if any, from an AppD entry. The best icon is determined based on the
 * compatibility that is requested (web or taskbar) based upon the image type and size. If no icon is
 * available then undefined is returned.
 *
 * @param appConfig - An AppD entry
 * @param compatibility - Either "taskbar" if the icon is intended for the MS Windows taskbar, or "web"
 * if the icon is intended for display in a browser (such as in the toolbar or titleBar)
 * @returns An IconDescriptor compatible with Finsemble's UI, or undefined if no acceptable icon is found.
 */
const maybeGetAppDIcon = (appConfig: ComponentConfig, compatibility: "taskbar" | "web"): IconDescriptor | undefined => {
	const iconsConfig: AppDIcon[] = appConfig?.appConfig?.icons;

	if (!iconsConfig) return;

	// Filter incompatible icons out of the AppD "icons" array
	const compatibleIcons = iconsConfig.filter((icon) => {
		if (compatibility === "taskbar" && isTaskbarCompatible(icon)) return true;
		return compatibility === "web" && isWebCompatible(icon);
	});

	if (compatibleIcons.length === 0) return;

	if (compatibleIcons.length === 1) {
		return {
			imageType: "url",
			path: absolutePath(appDIconSrc(compatibleIcons[0]), appConfig),
		};
	}

	return pickAppDIconBySize(compatibleIcons);
};

/**
 * Picks the best icons for Toolbar, TitleBar and TaskBar use based on an App's configuration.
 * If image paths or fonts cannot be derived from the app's config then an avatar
 * descriptor will be provided. Avatars are text based circles that the Finsemble UI can generate.
 *
 * @param appConfig - An App configuration ("component" format - not "appd" format)
 * @param defaultSystemIconURL - The default icon to use if no good match is found (the smart desktop
 * icon is generally used here).
 * @returns An object containing Finsemble UI a compatible IconDescriptor for each use case.
 */
const iconsFromAppConfig = (appConfig: ComponentConfig, defaultSystemIconURL?: string): AppIcons => {
	const displayName = appConfig?.component?.displayName ?? appConfig?.appConfig?.name;

	const initialsIcon: IconDescriptor = {
		imageType: "initials",
		name: displayName,
	};

	const defaultSystemIcon: IconDescriptor | undefined = defaultSystemIconURL
		? {
				imageType: "url",
				path: defaultSystemIconURL,
			}
		: undefined;

	const iconFromAppD: IconDescriptor | undefined = maybeGetAppDIcon(appConfig, "web");
	const iconFromAppDForTaskbar: IconDescriptor | undefined = maybeGetAppDIcon(appConfig, "taskbar");

	const legacyToolbarIcon = maybeLegacyToolbarIcon(appConfig);

	// Set the Toolbar and TitleBar icons. These are currently identical but may be further optimized in the future.
	// Legacy icon settings in the foreign.components.toolbar config will override icons
	// that are set in the appd entry. This allows `iconClass` to continue to be used.
	const toolbarIcon = legacyToolbarIcon ?? iconFromAppD ?? initialsIcon;
	const titleBarIcon = legacyToolbarIcon ?? iconFromAppD ?? initialsIcon;

	const ifURL = (icon?: IconDescriptor): IconDescriptor | undefined => (icon?.imageType === "url" ? icon : undefined);

	// Set the TaskBar icon
	// Taskbar only accepts compatible images (no fonticon or avatar)
	const taskbarIcon = ifURL(legacyToolbarIcon) ?? iconFromAppDForTaskbar ?? defaultSystemIcon;

	return {
		taskbarIcon,
		toolbarIcon,
		titleBarIcon,
	};
};

/**
 * Definitively computes the icons for the titlebar, taskbar and toolbar.
 *
 * Icons are derived from the app's AppD "icons" array unless the app's config contains
 * legacy overrides ("foreign.components.Toolbar").
 *
 * @param manifest - The system manifest
 * @param config - The app's config ("component" format - not "appd" format)
 */
export const getIcons = (manifest: Manifest, config: ComponentConfig): AppIcons => {
	const applicationIcon = manifest.startup_app?.applicationIcon ?? null;
	const systemDefaultIconURL = applicationIcon;

	const icons = iconsFromAppConfig(config, systemDefaultIconURL);
	Logger.debug(`Computed icons for app ${config.component?.type}`, icons);
	return icons;
};
